export interface Round {
  id: number;
  label: string;
  status: "draft" | "active" | "closed";
}

export interface RosterEntry {
  id: number;
  full_name: string;
  status: "pending" | "in_progress" | "done";
}

export interface SafeQuestion {
  id: number;
  dimension: number;
  prompt: string;
  options: string[];
}

export interface RadarPoint {
  dimension: number;
  label: string;
  correct: number;
  total: number;
  pct: number;
}

export interface AttemptResult {
  score: number;
  radar: RadarPoint[];
  rank: number | null;
  total_participants: number;
}

async function asJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T;
}

export async function fetchRound(): Promise<Round | null> {
  const body = await asJson<{ round: Round | null }>(await fetch("/api/round"));
  return body.round;
}

export async function fetchRoster(): Promise<RosterEntry[]> {
  const body = await asJson<{ roster: RosterEntry[] }>(await fetch("/api/roster"));
  return body.roster;
}

export async function startAttempt(personId: number): Promise<number> {
  const res = await fetch("/api/attempts", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ person_id: personId }),
  });
  const body = await asJson<{ attempt_id: number }>(res);
  return body.attempt_id;
}

export async function fetchNextQuestion(
  attemptId: number
): Promise<{ done: boolean; question?: SafeQuestion }> {
  return asJson(await fetch(`/api/attempts/${attemptId}/next`));
}

export async function submitAnswer(
  attemptId: number,
  questionId: number,
  chosenIndex: number
): Promise<{ is_correct: boolean; correct_index: number; explanation: string }> {
  const res = await fetch(`/api/attempts/${attemptId}/answer`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ question_id: questionId, chosen_index: chosenIndex }),
  });
  return asJson(res);
}

export async function finishAttempt(attemptId: number): Promise<AttemptResult> {
  const res = await fetch(`/api/attempts/${attemptId}/finish`, { method: "POST" });
  return asJson(res);
}

export interface Person {
  id: number;
  full_name: string;
}

export interface AnswerDetail {
  question_id: number;
  dimension: number;
  prompt: string;
  chosen_index: number;
  correct_index: number;
  is_correct: boolean;
}

export interface AttemptDetail {
  attempt: {
    id: number;
    round_id: number;
    person_id: number;
    started_at: number;
    finished_at: number | null;
    score: number | null;
  };
  radar: RadarPoint[];
  answers: AnswerDetail[];
}

function adminHeaders(passphrase: string, json = false): Record<string, string> {
  const headers: Record<string, string> = { "x-admin-passphrase": passphrase };
  if (json) headers["content-type"] = "application/json";
  return headers;
}

export async function adminLogin(passphrase: string): Promise<boolean> {
  const res = await fetch("/admin/api/login", {
    method: "POST",
    headers: adminHeaders(passphrase, true),
    body: JSON.stringify({ passphrase }),
  });
  return res.ok;
}

export async function adminListPeople(passphrase: string): Promise<Person[]> {
  const body = await asJson<{ people: Person[] }>(
    await fetch("/admin/api/people", { headers: adminHeaders(passphrase) })
  );
  return body.people;
}

export async function adminAddPeopleBulk(passphrase: string, names: string): Promise<number> {
  const body = await asJson<{ added: number }>(
    await fetch("/admin/api/people/bulk", {
      method: "POST",
      headers: adminHeaders(passphrase, true),
      body: JSON.stringify({ names }),
    })
  );
  return body.added;
}

export async function adminListRounds(passphrase: string): Promise<Round[]> {
  const body = await asJson<{ rounds: Round[] }>(
    await fetch("/admin/api/rounds", { headers: adminHeaders(passphrase) })
  );
  return body.rounds;
}

export async function adminCreateRound(passphrase: string, label: string): Promise<Round> {
  const body = await asJson<{ round: Round }>(
    await fetch("/admin/api/rounds", {
      method: "POST",
      headers: adminHeaders(passphrase, true),
      body: JSON.stringify({ label }),
    })
  );
  return body.round;
}

export async function adminActivateRound(passphrase: string, roundId: number): Promise<void> {
  await fetch(`/admin/api/rounds/${roundId}/activate`, {
    method: "POST",
    headers: adminHeaders(passphrase),
  });
}

export async function adminCloseRound(passphrase: string, roundId: number): Promise<void> {
  await fetch(`/admin/api/rounds/${roundId}/close`, {
    method: "POST",
    headers: adminHeaders(passphrase),
  });
}

export async function adminGetProgress(passphrase: string, roundId: number): Promise<RosterEntry[]> {
  const body = await asJson<{ progress: RosterEntry[] }>(
    await fetch(`/admin/api/rounds/${roundId}/progress`, { headers: adminHeaders(passphrase) })
  );
  return body.progress;
}

export async function adminGetPersonDetail(
  passphrase: string,
  roundId: number,
  personId: number
): Promise<AttemptDetail | null> {
  const body = await asJson<{ detail: AttemptDetail | null }>(
    await fetch(`/admin/api/rounds/${roundId}/person/${personId}`, {
      headers: adminHeaders(passphrase),
    })
  );
  return body.detail;
}

export async function adminGetTeamRadar(passphrase: string, roundId: number): Promise<RadarPoint[]> {
  const body = await asJson<{ radar: RadarPoint[] }>(
    await fetch(`/admin/api/rounds/${roundId}/team-radar`, { headers: adminHeaders(passphrase) })
  );
  return body.radar;
}
